import { useState, useRef } from 'react';
import { UploadCloud, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { compressImage } from '@/lib/compress';
import { useAssets } from '@/hooks/useAssets';
import { useAssetAnalysis } from '@/hooks/useCopywriting';
import { uploadToR2 } from '@/lib/r2';
import { useAuth } from '@/lib/db';
import { createSemaphore } from '@/lib/semaphore';
import { toast } from 'sonner';
import { captureError } from '@/lib/sentry.tsx';

// 限制同时处理（压缩+上传）的任务数为 6，避免大量文件同时压缩导致内存/CPU 压力
const processingSemaphore = createSemaphore(6);

interface AssetUploaderProps {
  projectId: string;
  folderId?: string | null;
}

export function AssetUploader({ projectId, folderId }: AssetUploaderProps) {
  const [totalFilesProcessing, setTotalFilesProcessing] = useState(0);
  const [completedFilesProcessing, setCompletedFilesProcessing] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addAssets, updateAssetsAnalysis, updateMultipleAnalysisStatus } = useAssets(projectId);
  const { analyzeAssets } = useAssetAnalysis();
  const auth = useAuth();

  // 统一的处理状态（用于显示进度胶囊）
  const isBusy = totalFilesProcessing > 0;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      await handleUpload(files);
    }
  };

  const processAnalysisBatch = async (batch: Array<{ id: string; url: string; filename: string; title?: string }>) => {
    try {
      const analysisResult = await analyzeAssets(batch);
      
      if (analysisResult?.analyses && analysisResult.analyses.length > 0) {
        await updateAssetsAnalysis(
          analysisResult.analyses.map((item) => {
            // 智能命名逻辑：优先使用 AI 建议的标题
            const originalName = batch.find(b => b.id === item.id)?.filename || '';
            const nameWithoutExt = originalName.replace(/\.[^.]+$/, '');
            
            // 如果 AI 给出了建议标题，优先使用；否则使用原文件名
            const finalTitle = item.suggested_title || nameWithoutExt;

            return {
              id: item.id,
              description: item.description,
              title: finalTitle,
              tags: item.tags || [],
              scene: item.scene,
              mood: item.mood,
              composition: item.composition,
              pure_hue: item.pure_hue,
              tonality: item.tonality,
            };
          })
        );
      }
    } catch (analysisError) {
      captureError(analysisError, { feature: 'asset-upload', action: 'analyze-batch', batchSize: batch.length });
      console.error('批次分析失败:', analysisError);
      // 分析失败时，批量更新状态为 failed（避免多次事务导致超时）
      await updateMultipleAnalysisStatus(
        batch.map(record => record.id),
        'failed'
      );
      toast.error('部分素材分析失败，可手动重新分析');
    }
  };

  const handleUpload = async (files: File[]) => {
    // 过滤非图片文件
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast.error('请上传图片文件');
      return;
    }

    // 数量限制校验（在压缩前立即检查）
    const MAX_FILE_COUNT = 100;
    if (imageFiles.length > MAX_FILE_COUNT) {
      toast.error(`上传失败：最多支持 ${MAX_FILE_COUNT} 张图片，当前选择了 ${imageFiles.length} 张`);
      return;
    }

    try {
      // 1. 获取认证 Token
      const token = auth.user?.refresh_token;
      if (!token) {
        throw new Error('未登录或登录已过期');
      }

      // 更新总进度计数
      setTotalFilesProcessing(prev => prev + imageFiles.length);

      const BATCH_SIZE = 6;
      let successfulResults: Array<any> = [];

      // 辅助函数：将一批结果写入数据库并启动分析
      const saveBatch = async (results: Array<any>) => {
        if (results.length === 0) return;
        
        try {
          const assetRecords = await addAssets(
            results.map((file) => ({
              filename: file.name,
              url: file.ufsUrl,
              thumbnail_url: file.ufsUrl,
              analysis_status: 'analyzing' as const,
              folder_id: folderId || null,
            }))
          );
          
          // 立即启动这一批的 AI 分析
          processAnalysisBatch(assetRecords);
          
          console.log(`[Uploader] 成功写入一批数据: ${results.length} 条`);
        } catch (err) {
          console.error('[Uploader] 批量写入数据库失败:', err);
          toast.error('部分素材保存到数据库失败');
        }
      };

      // 2. 执行流水线处理：[单张压缩 -> 单张上传]
      // 我们启动所有任务，但内部由信号量控制物理并发
      const uploadPromises = imageFiles.map(async (file) => {
        await processingSemaphore.acquire();
        try {
          // A. 压缩
          const compressedFile = await compressImage(file);
          // B. 上传
          const res = await uploadToR2([compressedFile], token);
          const result = res[0];
          
          if (result) {
            successfulResults.push(result);
            // 检查是否达到批次大小
            if (successfulResults.length >= BATCH_SIZE) {
              const batchToSave = [...successfulResults];
              successfulResults = []; // 清空缓冲区
              await saveBatch(batchToSave);
            }
          }
          
        } catch (err) {
          console.error(`文件 ${file.name} 处理失败:`, err);
        } finally {
          setCompletedFilesProcessing(prev => prev + 1);
          processingSemaphore.release();
        }
      });

      // 等待所有上传任务完成
      await Promise.all(uploadPromises);

      // 3. 处理最后一批（不足 BATCH_SIZE 的部分）
      if (successfulResults.length > 0) {
        await saveBatch(successfulResults);
      }

      toast.success(`上传处理完成：共选择 ${imageFiles.length} 张`);

    } catch (error) {
      captureError(error, { feature: 'asset-upload', action: 'upload', fileCount: imageFiles.length });
      console.error(error);
      const message = error instanceof Error ? error.message : '上传失败，请重试';
      toast.error(message);
    } finally {
      // 注意：这里不直接重置进度，而是让进度条在所有任务完成后自然消失
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 当所有任务处理完成后，重置进度计数
  if (isBusy && completedFilesProcessing >= totalFilesProcessing) {
    setTimeout(() => {
      setTotalFilesProcessing(0);
      setCompletedFilesProcessing(0);
    }, 1000);
  }

  return (
    <div className="flex items-center gap-4">
       <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
      />
      
      <Button 
        onClick={() => fileInputRef.current?.click()} 
        className="gap-2"
      >
        <UploadCloud className="w-4 h-4" />
        上传
      </Button>

      {/* 统一进度展示模块 */}
      {isBusy && (
        <div className="flex items-center gap-2 text-sm text-blue-600 animate-pulse bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100 shadow-sm transition-all duration-300">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>
            正在处理素材 ({completedFilesProcessing}/{totalFilesProcessing})
          </span>
        </div>
      )}
    </div>
  );
}
